import React, { useState, useEffect } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment/moment";
import Breadcrumb from "../../components/common/BreadCrumb";
import {
  DatePicker,
  DefaultButton,
  DefaultPrimaryButton,
  Input,
  InputWithLabel,
  SelectBox,
} from "../../components/micro/input";
import { InputError } from "../../components/error/error";
import { Eye, EyeSlash } from "../../assets/svg";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { useDispatch, useSelector } from "react-redux";
import { getAllRoleList } from "../../redux/accounts/slice";
import { getCountryData } from "../../redux/address/slice";
import { createStaffs, getStaff } from "../../redux/staffs/slice";

const CreateEditStaffAccount = () => {
  const [passwordType, setPasswordType] = useState("password");
  const [confirmPasswordType, setConfirmPasswordType] = useState("password");
  const [countryArray, setCountryArray] = useState([]);
  const [allRoleListArray, setAllRoleListArray] = useState([]);
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const staffData = location?.state?.staff;
  const isEditMode = location?.state?.mode;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [resetForm, setResetForm] = useState("");

  // Selector
  const {
    staffDetails,
    staffLoading,
    countryData,
    countryLoading,
    allRoleList,
    allRoleLoading,
  } = useSelector((store) => ({
    // Staff Details
    staffDetails: store?.staffsData?.getStaffStatus?.data,
    staffLoading: store?.staffsData?.loading,
    // Country Data
    countryData: store?.addressData?.getCountryDataStatus?.data,
    countryLoading: store?.addressData?.getCountryDataStatus?.loading,
    // all RoleL ist
    allRoleList: store?.accountsData?.getAllRoleListStatus?.data,
    allRoleLoading: store?.accountsData?.getAllRoleListStatus?.loading,
  }));

  // get Staff and Country
  useEffect(() => {
    if (staffData?.id) {
      dispatch(getStaff(staffData?.id));
    }
  }, [staffData]);

  useEffect(() => {
    dispatch(getCountryData());
    dispatch(getAllRoleList());
  }, [dispatch]);

  useEffect(() => {
    if (countryData?.data) {
      let data = countryData?.data?.map((x) => ({
        ...x,
        label: x.id,
      }));
      setCountryArray(data);
    }
    if (allRoleList?.data) {
      let data = allRoleList.data?.map((x) => ({
        ...x,
        label: x.title,
        value: x,
      }));
      setAllRoleListArray(data);
    }
  }, [countryData, allRoleList]);

  const countryValueGet = {
    label: staffDetails?.data?.country?.name,
    ...staffDetails?.data?.country,
  };

  const roleValueGet = {
    label: staffDetails?.data?.roles?.title,
    ...staffDetails?.data?.roles,
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    const payload = {
      full_name: formValues.fullName,
      email: formValues.email,
      gender: formValues.gender,
      dob: moment(formValues.dob).format("DD/MM/YYYY"),
      phone: formValues.phone,
      country_id: formValues.country.id,
      role: formValues.role.id,
    };
    if (!staffData?.id) {
      payload.password = formValues.password;
      payload.confirmPassword = formValues.confirmPassword;
    }

    if (staffData?.id) {
      const passwordString = String(formValues.password);
      if (passwordString.length > 0) {
        payload.password = formValues.password;
        payload.confirmPassword = formValues.confirmPassword;
      }
    } else {
      payload.password = formValues.password;
      payload.confirmPassword = formValues.confirmPassword;
    }

    const action = staffData?.id
      ? createStaffs({ id: staffData.id, ...payload })
      : createStaffs(payload);

    try {
      const res = await dispatch(action);
      if (res.type === "createStaffs/fulfilled") {
        navigate("/staff-accounts");
      }
    } catch (error) {
      console.error("Error creating staff:", error);
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  return (
    <>
      <div className="mb-5">
        <h6 className="font-Montserrat font-medium text-xl mb-3">
          {isEditMode == "edit" ? "Edit Staff Account" : "Create Staff Account"}
        </h6>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={
            isEditMode === "edit"
              ? "Edit Staff Account"
              : "Create Staff Account"
          }
          breadCrumbParent2={"Staff Account"}
          breadCrumbTitleKey={"/"}
          classes=""
        />
      </div>
      <div className="w-full bg-bgWhite  shadow drop-shadow-md rounded-lg py-5 px-6">
        <Formik
          enableReinitialize={true}
          initialValues={{
            fullName: staffData?.id ? staffData?.full_name : "",
            email: staffData?.id ? staffData?.email : "",
            gender: staffData?.id ? staffData?.gender : "",
            dob: staffData?.id ? staffData?.dob : "",
            phone: staffData?.id ? staffData?.phone : "",
            country: staffData?.id
              ? staffData?.country_id
                ? countryValueGet
                : ""
              : "",
            role: staffData?.id ? (staffData?.role ? roleValueGet : "") : "",
            password: "",
            confirmPassword: "",
          }}
          validationSchema={Yup.object().shape({
            fullName: Yup.string().trim().min(3).required("Please enter name"),
            email: Yup.string().min(3).required("Please enter email"),
            gender: Yup.string().required("Please select your gender"),
            dob: Yup.string().nullable().required("Please select birth date"),
            phone: Yup.string()
              .trim()
              .required("Please enter mobile number")
              .min(8, "Mobile is too short - should be 8 chars Minium.")
              .max(15, "Mobile is too long - should be 15 chars maximum."),
            role: Yup.object().required("Please select role"),
            country: Yup.object().required("Please select country"),
            password: staffData?.id
              ? Yup.string()
                  .trim()
                  .min(8, "Password is too short - should be 8 chars minimum.")
                  .matches(
                    /[a-zA-Z]/,
                    "Password can only contain Latin letters."
                  )
              : Yup.string()
                  .trim()
                  .required("Please enter password")
                  .min(8, "Password is too short - should be 8 chars minimum.")
                  .matches(
                    /[a-zA-Z]/,
                    "Password can only contain Latin letters."
                  ),
            confirmPassword: staffData?.id
              ? Yup.string()
                  .trim()
                  .oneOf([Yup.ref("password"), null], "Passwords must match")
              : Yup.string()
                  .trim()
                  .required("Please enter confirm password")
                  .oneOf([Yup.ref("password"), null], "Passwords must match"),
          })}
          onSubmit={(values, { resetForm }) => {
            setFormValues(values);
            setShowConfirmationModal(true);
            setResetForm(() => resetForm);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-6 mb-4">
                {/* Name */}
                <InputWithLabel
                  className={`${
                    isEditMode === "edit" && !isEditing
                      ? "text-textGrey border border-dashed "
                      : "text-black border hover:border-themeBtn"
                  }  ${
                    errors.fullName && touched.fullName
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  }  transition duration-300 ease-in-out`}
                  idFromName="fullName"
                  label="Name"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.fullName}
                  errors={errors.fullName && touched.fullName}
                  errorsText={errors.fullName}
                  disabled={isEditMode == "edit" && !isEditing}
                />
                {/* Email */}
                <InputWithLabel
                  className={`${
                    isEditMode === "edit" && !isEditing
                      ? "text-textGrey border border-dashed "
                      : "text-black border hover:border-themeBtn"
                  }  ${
                    errors.email && touched.email
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  }  transition duration-300 ease-in-out`}
                  idFromName="email"
                  label="Email"
                  type="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  errors={errors.email && touched.email}
                  errorsText={errors.email}
                  disabled={isEditMode == "edit" && !isEditing}
                />
                {/* Gender */}
                <div>
                  <div className="flex items-center space-x-3">
                    <label
                      form="gender"
                      className="text-textSecondary text-base"
                    >
                      Gender
                    </label>
                    <div>
                      <label htmlFor="male" className="relative cursor-pointer">
                        <Input
                          type="radio"
                          id="male"
                          name="gender"
                          value="male"
                          className="sr-only peer"
                          checked={values.gender === "male"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={isEditMode == "edit" && !isEditing}
                        />
                        <div
                          className="px-4 py-1 border border-themeBtn text-themeBtn rounded-full 
                          hover:bg-disableThemeBtn hover:border-themeBtn hover:text-themeBtn peer-checked:bg-themeBtn peer-checked:text-white peer-checked:border-themeBtn"
                        >
                          Male
                        </div>
                      </label>
                    </div>
                    <div>
                      <label
                        htmlFor="female"
                        className="relative cursor-pointer"
                      >
                        <Input
                          type="radio"
                          id="female"
                          value="female"
                          name="gender"
                          className="sr-only peer"
                          checked={values.gender === "female"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={isEditMode == "edit" && !isEditing}
                        />
                        <div
                          className="px-4 py-1 border border-themeBtn text-themeBtn rounded-full 
                          hover:bg-disableThemeBtn hover:border-themeBtn hover:text-themeBtn peer-checked:bg-themeBtn peer-checked:text-white peer-checked:border-themeBtn"
                        >
                          Female
                        </div>
                      </label>
                    </div>
                    <div>
                      <label
                        htmlFor="other"
                        className="relative cursor-pointer"
                      >
                        <Input
                          type="radio"
                          id="other"
                          name="gender"
                          value="other"
                          className="sr-only peer"
                          checked={values.gender === "other"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={isEditMode == "edit" && !isEditing}
                        />
                        <div
                          className="px-4 py-1 border border-themeBtn text-themeBtn rounded-full 
                          hover:bg-disableThemeBtn hover:border-themeBtn hover:text-themeBtn peer-checked:bg-themeBtn peer-checked:text-white peer-checked:border-themeBtn"
                        >
                          Other
                        </div>
                      </label>
                    </div>
                  </div>
                  {errors.gender && touched.gender ? (
                    <>
                      <InputError errorTitle={errors.gender} />
                    </>
                  ) : null}
                </div>
                {/* Birth Date */}
                <div>
                  <div>
                    <DatePicker
                      value={values.dob ? moment(values.dob) : null}
                      onChange={(date) =>
                        setFieldValue("dob", date ? date.toDate() : null)
                      }
                      disabled={isEditMode == "edit" && !isEditing}
                      format="DD/MM/YYYY"
                      maxDate={moment()}
                      error={errors.dob}
                    />
                  </div>
                  {errors.dob && touched.dob ? (
                    <InputError errorTitle={errors.dob} />
                  ) : null}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {/* Country */}
                  <div>
                    <div
                      className={`${
                        isEditMode === "edit" && !isEditing
                          ? "text-textGrey border border-dashed "
                          : "text-black hover:border-themeBtn"
                      }  ${
                        errors.country && touched.country
                          ? ` border-red border`
                          : ` border-borderTheme/80`
                      } relative bg-white transition duration-300 rounded plus-number-inputs`}
                    >
                      <SelectBox
                        id="country"
                        placeholder="Country"
                        options={countryArray}
                        isLoading={countryLoading}
                        getOptionValue={(countryArray) => countryArray.id}
                        disabled={isEditMode == "edit" && !isEditing}
                        value={
                          countryArray?.length > 0 &&
                          countryArray?.find((op) => {
                            return op.id === values?.country?.id;
                          })
                        }
                        onChange={(e) => {
                          setFieldValue("country", e);
                        }}
                      />
                      <label
                        htmlFor="country"
                        className="absolute text-light_grey pointer-events-none text-base bg-white duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] px-2 peer-focus:px-2 peer-focus peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1"
                      >
                        Country
                      </label>
                    </div>
                    {errors.country && touched.country ? (
                      <InputError errorTitle={errors.country} />
                    ) : null}
                  </div>
                  {/* Phone */}
                  <div className="col-span-2">
                    <InputWithLabel
                      className={`${
                        isEditMode === "edit" && !isEditing
                          ? "text-textGrey border border-dashed "
                          : "text-black border hover:border-themeBtn"
                      }  ${
                        errors.phone && touched.phone
                          ? `border border-red`
                          : `border border-borderTheme/80 focus-within:border-themeBtn `
                      }  transition duration-300 ease-in-out`}
                      idFromName="phone"
                      label="Mobile"
                      type={"number"}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.phone}
                      errors={errors.phone && touched.phone}
                      errorsText={errors.phone}
                      disabled={isEditMode == "edit" && !isEditing}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                </div>
                {/* Role */}
                <div>
                  <div
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black hover:border-themeBtn"
                    }  ${
                      errors.role && touched.role
                        ? ` border-red border`
                        : ` border-borderTheme/80`
                    } relative bg-white transition duration-300 rounded plus-number-inputs`}
                  >
                    <SelectBox
                      id="role"
                      placeholder="Role"
                      options={allRoleListArray}
                      getOptionValue={(selectedRole) => selectedRole.value}
                      getOptionLabel={(selectedRole) => selectedRole?.label}
                      value={
                        values?.role && allRoleListArray?.length > 0
                          ? allRoleListArray.find(
                              (op) => op.label === values.role.label
                            ) || null
                          : null
                      }
                      onChange={(value) => {
                        setFieldValue("role", value);
                      }}
                      styles={{
                        control: (base) => ({
                          ...base,
                          border: `1px solid #0000003B`,
                          borderRadius: "0.3rem",

                          boxShadow: "none",
                          "&:hover": {
                            border: "1px solid #0000003B",
                          },
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected
                            ? "#F15B29"
                            : "white",
                          "&:active": {
                            backgroundColor: state.isSelected
                              ? "#F15B29"
                              : "rgba(0, 0, 0, 0.04)",
                          },
                        }),
                      }}
                      onBlur={handleBlur}
                      disabled={isEditMode == "edit" && !isEditing}
                    />
                  </div>
                  {errors.role && touched.role ? (
                    <InputError errorTitle={errors.role} />
                  ) : null}
                </div>

                {/* Password */}
                <div>
                  <InputWithLabel
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black border hover:border-themeBtn"
                    }  ${
                      errors.password && touched.password
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                    idFromName="password"
                    label="Password"
                    type={passwordType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    errors={errors.password && touched.password}
                    errorsText={errors.password}
                    disabled={isEditMode == "edit" && !isEditing}
                    icon={
                      <p
                        className="p-2 absolute top-1.5 right-0 rounded bg-transparent"
                        onClick={() => {
                          if (passwordType === "password") {
                            setPasswordType("text");
                            return;
                          }
                          setPasswordType("password");
                        }}
                      >
                        {passwordType === "password" ? <EyeSlash /> : <Eye />}
                      </p>
                    }
                  />
                </div>
                {/* Confirm Password */}
                <div>
                  <InputWithLabel
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black border hover:border-themeBtn"
                    }  ${
                      errors.confirmPassword && touched.confirmPassword
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                    idFromName="confirmPassword"
                    label="Confirm Password"
                    type={confirmPasswordType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.confirmPassword}
                    errors={errors.confirmPassword && touched.confirmPassword}
                    errorsText={errors.confirmPassword}
                    disabled={isEditMode == "edit" && !isEditing}
                    icon={
                      <p
                        className="p-2 absolute top-1.5 right-0 rounded bg-transparent"
                        onClick={() => {
                          if (confirmPasswordType === "password") {
                            setConfirmPasswordType("text");
                            return;
                          }
                          setConfirmPasswordType("password");
                        }}
                      >
                        {confirmPasswordType === "password" ? (
                          <EyeSlash />
                        ) : (
                          <Eye />
                        )}
                      </p>
                    }
                  />
                </div>
              </div>

              {/* checkbox */}
              <div>
                <button type="button" className="flex items-center">
                  <input
                    type="checkbox"
                    id={"salesStaff"}
                    name={"Sales Staff"}
                    className="w-4 h-4 mr-2 accent-themeBtn "
                    checked={values.salesStaff}
                    disabled={isEditMode == "edit" && !isEditing}
                    onChange={() =>
                      setFieldValue("salesStaff", !values.salesStaff)
                    }
                  />
                  <p
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey font-normal text-base opacity-80 font-Proxima"
                        : "text-primaryBlack font-normal text-base font-Proxima"
                    }`}
                  >
                    Sales Staff
                  </p>
                </button>
              </div>
              {/* Submit button */}
              <div className="flex justify-end mt-3">
                {isEditMode === "edit" ? (
                  isEditing ? (
                    <>
                      <div className="flex gap-2">
                        <DefaultPrimaryButton
                          type="submit"
                          value="Cancel"
                          onClick={() => navigate("/staff-accounts")}
                        />
                        <DefaultButton type="submit" value="Save" />
                      </div>
                    </>
                  ) : (
                    <DefaultButton
                      value="Edit"
                      onClick={handleEditClick}
                      type="submit"
                    />
                  )
                ) : (
                  <DefaultButton type="submit" value="confirm" />
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>
      {showConfirmationModal && (
        <div>
          <ConfirmationModel
            confirmText={isEditMode === "edit" ? "Edit" : "Create"}
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={() => handleConfirm(resetForm)}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default CreateEditStaffAccount;
